import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef, useMemo } from "react";
import Select from "react-select";
import { API_URL, LOCATION_ID, TOKEN } from "..";
import "mapbox-gl/dist/mapbox-gl.css";
import Swal from "sweetalert2";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import axios from "axios";
import currency from "../helper/currency.json";

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoiemFtcCIsImEiOiJjbTkxa3dkcnMwMmQxMnJzYzBobW9hOHBiIn0.GWvvqPrWqtMby0HYCYdjMQ";

const QuoteEditLayer = ({ invoiceData: initialInvoiceData }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [optionsForServices, setOptionsForServices] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyData, setCompanyData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    vatRegNo: "",
    logo: "",
    accountNumber: "",
    accountTitle: "",
    sortCode: "",
  });
  const [customers, setCustomers] = useState([]);
  const [footerContent, setFooterContent] = useState(null);

  const [customerType, setCustomerType] = useState("existing");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [invoiceData, setInvoiceData] = useState({
    id: "",
    invoiceNumber: "",
    orderNumber: "",
    issuedDate: "",
    dueDate: "",
    customerName: "",
    customerAddress: "",
    customerPhone: "",
    email: "",
    postcode: "",
    latitude: "",
    longitude: "",
    type_of_invoice: "",
  });
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [newItem, setNewItem] = useState({
    itemType: "service",
    itemName: null,
    qty: "",
    unitPrice: "",
    description: "",
  });
  const [discount, setDiscount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [stockList, setStockList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const addressInputRef = useRef(null);
  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  // Memoize privileges to prevent re-creation on every render
  const privileges = useMemo(() => {
    const storedPrivileges = localStorage.getItem("privilages");
    return storedPrivileges
      ? JSON.parse(storedPrivileges)
      : { quotes: { edit: false, read: false, delete: false, changeStatus: false } };
  }, []);

  // Check if the user has edit privileges
  const canEdit = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.roles?.role === "admin" || privileges?.quotes?.edit;
  }, [privileges]);

  // Currency options
  async function getAllCurrencyOptions() {
    try {
      const mapped = currency.map((e) => ({
        value: e.abbreviation,
        label: e.currency,
      }));
      return mapped;
    } catch (error) {
      console.error("Error fetching currency list:", error);
      return [];
    }
  }

  // Initialize Quill editor
  useEffect(() => {
    if (currentStep === 3 && quillRef.current && !quillInstance.current && canEdit) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: "" }, { align: "center" }, { align: "right" }],
          ],
        },
        placeholder: "Enter item description",
      });

      quillInstance.current.root.setAttribute("dir", "ltr");
      quillInstance.current.root.setAttribute("lang", "en");

      quillInstance.current.on("text-change", () => {
        const content = quillInstance.current.root.innerHTML;
        setNewItem((prev) => ({ ...prev, description: content }));
        quillInstance.current.focus();
      });

      quillInstance.current.focus();
    }

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off("text-change");
        quillInstance.current = null;
      }
    };
  }, [currentStep, canEdit]);

  // Update Quill content when description changes
  useEffect(() => {
    if (quillInstance.current && newItem.description && canEdit) {
      if (quillInstance.current.root.innerHTML !== newItem.description) {
        quillInstance.current.root.innerHTML = newItem.description;
        quillInstance.current.focus();
      }
    }
  }, [newItem.description, canEdit]);

  // Initialize data and currency options
  useEffect(() => {
    const initialize = async () => {
      const currencyOptions = await getAllCurrencyOptions();
      setCurrencyOptions(currencyOptions);

      // Set initial currency from initialInvoiceData or default to PKR
      const initialCurrency = currencyOptions.find(
        (option) => option.value === initialInvoiceData.currency
      ) || currencyOptions.find((option) => option.value === "PKR");
      setSelectedCurrency(initialCurrency);

      fetchCompanies();
      fetchCustomers();
      fetchStock();
      fetchServices();
      initializeInvoiceData();
    };
    initialize();
  }, []);

  const fetchServices = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
    } else {
      try {
        const response = await fetch(`${API_URL}/api/service?locationId=${LOCATION_ID}`, {
          method: "GET",
          headers: { Accept: "*/*", "Content-Type": "application/json" },
        });
        const data = await response.json();
        const formatted = data.map((e) => ({ label: e.title, value: e.id }));
        setOptionsForServices(formatted);
      } catch (error) {
        setAlert({
          show: true,
          type: "danger",
          message: "Failed to fetch services items",
        });
      }
    }
  };

  const onChangeService = async (value) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
    } else {
      try {
        const response = await fetch(`${API_URL}/api/service/${value.value}`, {
          method: "GET",
          headers: { Accept: "*/*", "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch service details");
        const data = await response.json();
        setNewItem({
          ...newItem,
          itemName: value,
          qty: "1",
          description: data.description,
          unitPrice: data.price,
        });
      } catch (error) {
        setAlert({
          show: true,
          type: "danger",
          message: "Failed to fetch service details",
        });
      }
    }
  };

  const initializeInvoiceData = () => {
    setInvoiceData({
      id: initialInvoiceData.id,
      invoiceNumber: initialInvoiceData.invoice_number,
      orderNumber: initialInvoiceData.order_number,
      issuedDate: initialInvoiceData.issue_date,
      dueDate: initialInvoiceData.due_date,
      customerName: initialInvoiceData.customer_name,
      customerAddress: initialInvoiceData.address,
      customerPhone: initialInvoiceData.phone,
      type_of_invoice: initialInvoiceData.type_of_invoice,
      email: initialInvoiceData.email || "",
      postcode: initialInvoiceData.postcode || "",
      latitude: "",
      longitude: "",
    });

    const company = initialInvoiceData.company;
    const companyOption = {
      value: company.id,
      label: company.name,
      name: company.name,
      address: company.address,
      email: company.email,
      phone: company.phone,
      vat: company.vat,
      logo: company.logo ? `${API_URL}${company.logo}` : "",
    };
    setSelectedCompany(companyOption);
    setCompanyData({
      name: company.name,
      address: company.address,
      email: company.email,
      phone: company.phone,
      vatRegNo: company.vat,
      logo: company.logo ? `${API_URL}${company.logo}` : "",
      accountNumber: company.accountNumber || "",
      sortCode: company.sortCode || "",
      accountTitle: company.accountTitle || "",
    });

    const items = initialInvoiceData.items.map((item, index) => {
      const isStockItem = stockList.some((stock) => stock.label === item.item_name);
      const itemName = { label: item.item_name, value: item.item_name };
      return {
        id: item.id || index + 1,
        itemName,
        qty: item.quantity,
        unitPrice: item.unit_price,
        price: item.price.toFixed(2),
        itemType: isStockItem ? "stock" : "service",
        description: item.description || "",
      };
    });
    setInvoiceItems(items);

    setDiscount(Number(initialInvoiceData.discount));
    setTaxAmount(Number(initialInvoiceData.tax));
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company?locationId=${LOCATION_ID}`, {
        method: "GET",
        headers: { Accept: "*/*", "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (response.status === 200) {
        if (!Array.isArray(data.companies)) {
          throw new Error("Invalid data format: companies is not an array");
        }
        setCompanies(
          data.companies.map((company) => ({
            value: company.id,
            label: company.name,
            ...company,
          }))
        );
      } else {
        throw new Error(data.message || "Failed to fetch companies");
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Failed to fetch companies: " + error.message,
      });
      setCompanies([]);
    }
  };

  const fetchCustomers = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
      return;
    }

    try {
      let allContacts = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const options = {
          method: "GET",
          url: "https://services.leadconnectorhq.com/contacts/",
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            Version: "2021-07-28",
            Accept: "application/json",
          },
          params: {
            locationId: LOCATION_ID,
            limit: 100,
            page: page,
          },
        };

        const response = await axios.request(options);
        const contacts = response.data.contacts || [];
        allContacts = [...allContacts, ...contacts];
        totalPages = response.data.meta?.totalPages || 1;
        page += 1;
      }

      let filteredContacts = [];
      if (user?.roles?.role === "admin") {
        filteredContacts = allContacts;
      } else {
        filteredContacts = allContacts.filter((item) => item.assignedTo === user?.id);
      }
      const customerList = filteredContacts.map((customer) => ({
        value: customer.id,
        label: `${customer.name || `${customer.firstName || ""} ${customer.lastName || ""}`.trim()} ${customer.phone || ""}`.trim(),
        name: customer.name || `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "Unknown",
        address: [customer.address1, customer.city, customer.state, customer.country].filter(Boolean).join(", ") || "",
        phone: customer.phone || "",
        email: customer.email || "",
        postcode: customer.postalCode || "",
      }));

      setCustomers(customerList);

      const matchedCustomer = customerList.find(
        (c) => c.name === initialInvoiceData.customer_name && c.phone === initialInvoiceData.phone
      );

      if (matchedCustomer) {
        setCustomerType("existing");
        setSelectedCustomer({
          value: matchedCustomer.id,
          label: matchedCustomer.label,
          name: matchedCustomer.name,
          address: matchedCustomer.address,
          phone: matchedCustomer.phone,
          email: matchedCustomer.email,
          postcode: matchedCustomer.postcode,
        });
        setInvoiceData((prev) => ({
          ...prev,
          email: matchedCustomer.email || "",
          postcode: matchedCustomer.postcode || "",
          customerName: matchedCustomer.name,
          customerAddress: matchedCustomer.address,
          customerPhone: matchedCustomer.phone,
        }));
      } else {
        setCustomerType("new");
      }
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Failed to fetch customers from GoHighLevel: " + error.message,
      });
      setCustomers([]);
    }
  };

  const fetchStock = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
    } else {
      try {
        const response = await fetch(`${API_URL}/api/invoice/stock/fetch?locationId=${LOCATION_ID}`, {
          method: "GET",
          headers: { Accept: "*/*", "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch stock");
        const data = await response.json();
        const formatted = data.stock.map((e) => ({
          label: e.title,
          value: e.id,
        }));
        setStockList(formatted);
      } catch (error) {
        setAlert({
          show: true,
          type: "danger",
          message: "Failed to fetch stock items",
        });
      }
    }
  };

  const onChangeStock = async (value) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
    } else {
      try {
        const response = await fetch(`${API_URL}/api/invoice/stock/fetch-single/${value.value}`, {
          method: "GET",
          headers: { Accept: "*/*", "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch stock details");
        const data = await response.json();
        setNewItem({
          ...newItem,
          itemName: value,
          unitPrice: data.stock.sellPrice,
          description: data.stock.description,
        });
      } catch (error) {
        setAlert({
          show: true,
          type: "danger",
          message: "Failed to fetch stock details",
        });
      }
    }
  };

  const handleCompanyChange = (selectedOption) => {
    if (!canEdit) return;
    setSelectedCompany(selectedOption);
    if (selectedOption) {
      setCompanyData({
        name: selectedOption.name || "",
        address: selectedOption.address || "",
        email: selectedOption.email || "",
        phone: selectedOption.phone || "",
        vatRegNo: selectedOption.vat || selectedOption.vatNo || "",
        logo: selectedOption.logo ? `${API_URL}${selectedOption.logo}` : "",
        accountNumber: selectedOption.accountNumber || "",
        accountTitle: selectedOption.accountTitle || "",
        sortCode: selectedOption.sortCode || "",
      });
    } else {
      setCompanyData({
        name: "",
        address: "",
        email: "",
        phone: "",
        vatRegNo: "",
        logo: "",
      });
    }
    setErrors({ ...errors, company: "" });
  };

  const handleCustomerChange = (selectedOption) => {
    if (!canEdit) return;
    setSelectedCustomer(selectedOption);
    if (selectedOption) {
      setInvoiceData({
        ...invoiceData,
        customerName: selectedOption.name || "",
        customerAddress: selectedOption.address || "",
        customerPhone: selectedOption.phone || "",
        email: selectedOption.email || "",
        postcode: selectedOption.postcode || "",
        latitude: "",
        longitude: "",
      });
    } else {
      setInvoiceData({
        ...invoiceData,
        customerName: "",
        customerAddress: "",
        customerPhone: "",
        email: "",
        postcode: "",
        latitude: "",
        longitude: "",
      });
    }
    setErrors({ ...errors, customer: "" });
  };

  const handleInvoiceChange = (field, value) => {
    if (!canEdit) return;
    setInvoiceData({ ...invoiceData, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const handleAddressSelect = (selected) => {
    if (!canEdit) return;
    if (selected) {
      setInvoiceData({
        ...invoiceData,
        customerAddress: selected.value,
        latitude: selected.coordinates[1],
        longitude: selected.coordinates[0],
      });
      setAddressSuggestions([]);
      setErrors({ ...errors, customerAddress: null });
    }
  };

  const fetchCoordinates = async (address) => {
    if (!address) return { latitude: "", longitude: "" };
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=GB&limit=1`
      );
      const data = await response.json();
      if (data.features?.length > 0) {
        const [longitude, latitude] = data.features[0].geometry.coordinates;
        return { latitude, longitude };
      }
      return { latitude: "", longitude: "" };
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return { latitude: "", longitude: "" };
    }
  };
  useEffect(() => {
    const fetchInvoicePrivacy = async () => {
      const api = await fetch(API_URL + '/api/user/fetch-quote/policy?locationId='+LOCATION_ID, { method: 'GET' });
      const json = await api.json();
      setFooterContent(json?.policy?.details);
    };
    fetchInvoicePrivacy();
  }, []);

  const validateStep = () => {
    const newErrors = {};
    if (currentStep === 1) {
      if (!selectedCompany) newErrors.company = "Please select a company";
    } else if (currentStep === 2) {
      if (!invoiceData.invoiceNumber) newErrors.invoiceNumber = "Invoice number is required";
      if (!invoiceData.issuedDate) newErrors.issuedDate = "Issued date is required";
      if (!selectedCurrency) newErrors.currency = "Currency is required";
      if (customerType === "existing") {
        if (!selectedCustomer) newErrors.customer = "Please select a customer";
      } else {
        if (!invoiceData.customerName) newErrors.customerName = "Customer name is required";
        if (!invoiceData.customerAddress) newErrors.customerAddress = "Customer address is required";
        if (!invoiceData.customerPhone) newErrors.customerPhone = "Customer phone is required";
        if (!invoiceData.email || !/\S+@\S+\.\S+/.test(invoiceData.email))
          newErrors.email = "Valid email is required";
        if (!invoiceData.postcode) newErrors.postcode = "Postcode is required";
      }
    } else if (currentStep === 3) {
      if (invoiceItems.length === 0) newErrors.items = "At least one item is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(currentStep + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleStepClick = (step) => {
    if (step <= currentStep) {
      setCurrentStep(step);
      setErrors({});
    }
  };

  const handleItemChange = (field, value) => {
    if (!canEdit) return;
    setNewItem({ ...newItem, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  const addItem = () => {
    if (!canEdit) {
      setAlert({
        show: true,
        type: "danger",
        message: "You do not have permission to edit items",
      });
      return;
    }
    const errors = {};
    if (!newItem.itemName) errors.itemName = "Item is required";
    if (!newItem.unitPrice) errors.unitPrice = "Unit price is required";
    if (newItem.itemType === "stock" && !newItem.qty) errors.qty = "Quantity is required";
    if (!newItem.description.trim()) errors.description = "Description is required";

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    const qty = newItem.itemType === "service" ? 1 : parseFloat(newItem.qty);
    setInvoiceItems([
      ...invoiceItems,
      {
        id: invoiceItems.length + 1,
        itemName: newItem.itemName,
        qty,
        unitPrice: parseFloat(newItem.unitPrice),
        price: (qty * parseFloat(newItem.unitPrice)).toFixed(2),
        itemType: newItem.itemType,
        description: newItem.description.trim(),
      },
    ]);
    setNewItem({
      itemType: "stock",
      itemName: null,
      qty: "",
      unitPrice: "",
      description: "",
    });
    if (quillInstance.current) {
      quillInstance.current.root.innerHTML = "";
      quillInstance.current.focus();
    }
    setErrors({});
  };

  const removeItem = (id) => {
    if (!canEdit) {
      setAlert({
        show: true,
        type: "danger",
        message: "You do not have permission to remove items",
      });
      return;
    }
    setInvoiceItems(invoiceItems.filter((item) => item.id !== id));
  };

  const calculateSubtotal = () => invoiceItems.reduce((acc, item) => acc + parseFloat(item.price), 0);

  const calculateDiscount = () => (calculateSubtotal() * discount) / 100;

  const calculateTax = () => ((calculateSubtotal() - calculateDiscount()) * taxAmount) / 100;

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscount();
    const tax = calculateTax();
    return subtotal - discountAmount + tax;
  };

  const createCustomer = async () => {
    if (!canEdit) throw new Error("You do not have permission to create customers");
    let latitude = invoiceData.latitude;
    let longitude = invoiceData.longitude;
    if (!latitude || !longitude) {
      const coords = await fetchCoordinates(invoiceData.customerAddress);
      latitude = coords.latitude;
      longitude = coords.longitude;
    }
    const obj = {
      name: invoiceData.customerName,
      address: invoiceData.customerAddress,
      phone: invoiceData.customerPhone,
      email: invoiceData.email,
      postcode: invoiceData.postcode,
      map_location: latitude && longitude ? `${latitude},${longitude}` : "",
    };
    try {
      const response = await fetch(`${API_URL}/api/customer/signup-without-password`, {
        method: "POST",
        body: JSON.stringify(obj),
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (response.status === 201) {
        return data.customer.id;
      } else {
        throw new Error(data.message || "Failed to create customer");
      }
    } catch (error) {
      throw new Error("Failed to create customer: " + error.message);
    }
  };

  const handleInvoiceSubmit = async () => {
    if (!canEdit) {
      setAlert({
        show: true,
        type: "danger",
        message: "You do not have permission to update quotes",
      });
      return;
    }
    if (validateStep()) {
      setIsSubmitting(true);
      try {
        let customerId = selectedCustomer.value;
        

        const obj = {
          id: invoiceData.id,
          invoice_number: invoiceData.invoiceNumber,
          customer_name: invoiceData.customerName,
          phone: invoiceData.customerPhone,
          address: invoiceData.customerAddress,
          email: invoiceData.email,
          issue_date: invoiceData.issuedDate,
          due_date: invoiceData.dueDate,
          order_number: invoiceData.orderNumber,
          amount: calculateTotal(),
          customerId:customerId,
          discount: discount,
          tax: taxAmount,
          job_id: null,
          items: invoiceItems.map((item) => ({
            id: item.id,
            item_name: item.itemName.label || item.itemName.value,
            quantity: item.qty,
            unit_price: item.unitPrice,
            price: item.price,
            description: item.description,
          })),
          company_id: selectedCompany.value,
          customer_id: customerId,
          type_of_invoice: invoiceData.type_of_invoice,
          currency: selectedCurrency?.value || "PKR", // Include currency in payload
        };

        const response = await fetch(`${API_URL}/api/invoice/update/${invoiceData.id}`, {
          method: "PUT",
          body: JSON.stringify(obj),
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();
        if (response.status === 200) {
          Swal.fire({
            title: "Quote",
            text: "Your quote is successfully updated.",
            icon: "success",
          });
          setAlert({
            show: true,
            type: "primary",
            message: "Quote updated successfully",
          });
        } else {
          setAlert({
            show: true,
            type: "danger",
            message: data.message || "Failed to update quote",
          });
        }
      } catch (error) {
        setAlert({
          show: true,
          type: "danger",
          message: error.message || "Network error occurred",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const professionalTemplate = `
  <!DOCTYPE html>
  <html lang="en">
  <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Quote {{invoice_number}}</title>
    <style>
      /* Ensure consistent styling for Quill content */
      .ql-align-center { text-align: center; }
      .ql-align-right { text-align: right; }
      ul, ol { margin-left: 20px; padding-left: 0; }
      li { margin-bottom: 5px; }
      td ol, td ul  {
          padding-left:30px;
          list-style:auto;
      }
      td p{
        margin-bottom:0px;
      }
    </style>
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 40px; font-size: 12px; color: #000;">
    <!-- Page 1 -->
    <div style="width: 100%; height: auto; margin: 0 auto; padding-top: 20px; box-sizing: border-box;">
        <!-- Header -->
        <table style="width: 100%; border-collapse: collapse;">
            <tr>
                <td style="width: 50%; vertical-align: top;padding-left:0px;">
                    <div style="font-weight: bold; font-size: 14px;">
                    <img src="{{business_logo}}" style="width:140px;"/>
                    </div>
                    <div style="font-weight: bold; font-size: 14px">{{business_header}}</div>
                    <div style="width:150px;font-size:13px">{{business_address}}</div>
                    <div style="font-size:13px;">{{business_email}}</div>
                    <div style="font-size:13px;">VAT NO: {{vat_reg_no}}</div>
                </td>
                <td style="width: 50%; text-align: right; vertical-align: top;padding-right:20px">
                    <div style="font-size:13px;">Issue Date: {{issue_date}}</div>
                </td>
            </tr>
        </table>
        <!-- Bill To -->
        <div style="text-align:center;padding-top:10px">
        <p style="font-weight: 600; font-size: 18px;">{{type}}</p>
        </div>
        <div style="border-top:3px solid #144BD6;margin-top:10px"/>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <tr>
                <td style="width: 70%; vertical-align: top;padding-left:10px">
                    <div style="font-weight: bold;">For:</div>
                    <div style="font-size:12px">{{customer_name}}</div>
                    <div style="font-size:12px">{{customer_phone}}</div>
                    <div style="font-size:12px">{{customer_address}}</div>
                </td>
                <td style="width: 30%;text-align:right;padding-right:20px">
                <div style="font-size:12px">Quote No: {{invoice_number1}}</div>
                {{order_number}}
                </td>
            </tr>
        </table>
        <!-- Description Table -->
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
            <tr style="background-color: #144BD6;">
                <th style="text-align: left; padding: 5px; border-top: 2px solid #144BD6;border-bottom: 2px solid #144BD6;color:white;font-weight:bold">Description</th>
                <th style="text-align: center; padding: 5px; border-top: 2px solid #144BD6;border-bottom: 2px solid #144BD6;color:white;font-weight:bold">Quantity</th>
                <th style="text-align: center; padding: 5px; border-top: 2px solid #144BD6;border-bottom: 2px solid #144BD6;color:white;font-weight:bold">Unit Price</th>
                <th style="text-align: right; padding: 5px; border-top: 2px solid #144BD6;border-bottom: 2px solid #144BD6;color:white;font-weight:bold">Cost</th>
            </tr>
            {{service_items}}
        </table>
        <!-- Payment Instructions -->
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px;padding:10px">
            <tr>
                <td style="width: 100%; vertical-align: top; text-align: right;">
                    <div style="padding:5px;background:#f3f3f3">Total Net Amount: {{currency}}{{subtotal}}</div>
                    {{discount}}
                    {{tax}}
                    <div style="border:1px solid #144BD6;"></div>
                    <div style="font-weight: bold;padding:5px">Quote Total: {{currency1}}{{total}}</div>
                    <div style="border:1px solid #144BD6;"></div>
                </td>
            </tr>
        </table>
    </div>
    <!-- Page 2 -->
    <div style="width: 100%; height: 842px; margin: 0px auto; padding: 10px; box-sizing: border-box; page-break-before: always;">
        <!-- Terms and Conditions -->
        <div style="margin-top: 20px;text-align:center">
          ${footerContent}
        </div>
        <!-- Signature -->
        <div style="margin-top: 40px;text-align:center">
            <div style="font-size:12px"><strong>{{business_footer}}</strong></div>
            <div style="font-size:12px">{{issue_date_footer}}</div>
            <div style="font-size:12px">Tel: {{business_phone}}</div>
        </div>
    </div>
</body>
</html>
`;

  const renderTemplate = () => {
    function checkOddEven(index) {
      return index % 2 === 0 ? "Even" : "Odd";
    }
    function convertDateFormat(dateString) {
      if (dateString) {
        const [day, month, year] = dateString?.split("-");
        return `${day}-${month}-${year}`;
      }
      return "N/A";
    }

    const serviceItems = invoiceItems
      .map((item, index) => {
        if (checkOddEven(index) === "Even") {
          return `<tr>
              <td style="padding: 5px; vertical-align: top;border: 1px dotted #c0c0c0;border-left: 1px dotted #c0c0c0;font-size:14px">${
                item.itemName?.label || item.itemName?.value || "N/A"
              }<br>
              ${item.description || "N/A"}
              </td>
              <td style="text-align: center; padding: 5px;border: 1px dotted #c0c0c0;font-size:14px">${item.qty}</td>
              <td style="text-align: center; padding: 5px;border: 1px dotted #c0c0c0;font-size:14px">${
                selectedCurrency?.value || "PKR"
              }${item.unitPrice.toFixed(2)}</td>
              <td style="text-align: right; padding: 5px;border: 1px dotted #c0c0c0;font-size:14px">${
                selectedCurrency?.value || "PKR"
              }${item.price}</td>
            </tr>`;
        } else {
          return `<tr style="background:#f3f3f3">
              <td style="padding: 5px; vertical-align: top;border: 1px dotted #c0c0c0;border-left: 1px dotted #c0c0c0;font-size:14px">${
                item.itemName?.label || item.itemName?.value || "N/A"
              }<br>
              ${item.description || "N/A"}
              </td>
              <td style="text-align: center; padding: 5px;border: 1px dotted #c0c0c0;font-size:14px">${item.qty}</td>
              <td style="text-align: center; padding: 5px;border: 1px dotted #c0c0c0;font-size:14px">${
                selectedCurrency?.value || "PKR"
              }${item.unitPrice.toFixed(2)}</td>
              <td style="text-align: right; padding: 5px;border: 1px dotted #c0c0c0;font-size:14px">${
                selectedCurrency?.value || "PKR"
              }${item.price}</td>
            </tr>`;
        }
      })
      .join("");
      console.log(invoiceData)
    return professionalTemplate
      .replace("{{business_logo}}", companyData.logo || "./assets/images/logo.png")
      .replace("{{business_header}}", companyData.name || "Your Company")
      .replace("{{business_address}}", companyData.address || "Your Address")
      .replace("{{business_phone}}", companyData.phone || "Your Phone")
      .replace("{{business_email}}", companyData.email || "Your Email")
      .replace("{{vat_reg_no}}", companyData.vatRegNo || "Your VAT Reg No")
      .replace("{{invoice_number}}", invoiceData.invoiceNumber || "N/A")
      .replace("{{invoice_number1}}", invoiceData.invoiceNumber || "")
      .replace(
        "{{order_number}}",
        invoiceData.orderNumber ? `<div>Order No: ${invoiceData.orderNumber}</div>` : ""
      )
      .replace("{{issue_date}}", convertDateFormat(invoiceData.issuedDate) || "N/A")
      .replace("{{issue_date_footer}}", convertDateFormat(invoiceData.issuedDate) || "N/A")
      .replace("{{due_date_of_invocie}}", invoiceData.dueDate ? convertDateFormat(invoiceData.dueDate) : "")
      .replace("{{call_number}}", "N/A")
      .replace("{{tow_reason}}", "N/A")
      .replace("{{type}}", "Quote")
      .replace("{{date_completed}}", "N/A")
      .replace("{{customer_name}}", invoiceData.customerName || "N/A")
      .replace("{{customer_address}}", invoiceData.customerAddress || "N/A")
      .replace("{{customer_phone}}", invoiceData.customerPhone || "N/A")
      .replace("{{authorized_by}}", "N/A")
      .replace("{{tow_from}}", "N/A")
      .replace("{{tow_to}}", "N/A")
      .replace("{{accountNumber}}", companyData.accountNumber || "N/A")
      .replace("{{sortCode}}", companyData.sortCode || "N/A")
      .replace("{{accountTitle}}", companyData.accountTitle)
      .replace("{{vehicle_color}}", "N/A")
      .replace("{{vehicle_vin}}", "N/A")
      .replace("{{vehicle_plate}}", "N/A")
      .replace("{{service_items}}", serviceItems)
      .replace("{{subtotal}}", calculateSubtotal().toFixed(2))
      .replace("{{currency}}", selectedCurrency?.value || "PKR")
      .replace("{{currency1}}", selectedCurrency?.value || "PKR")
      .replace(
        "{{discount}}",
        discount === 0
          ? '<div style="padding:15px;"></div>'
          : `<div style="padding:5px">Discount: ${selectedCurrency?.value || "PKR"}${calculateDiscount().toFixed(2)}</div>`
      )
      .replace(
        "{{tax}}",
        taxAmount !== 0
          ? `<div style="padding:5px;">Total VAT Amount: ${selectedCurrency?.value || "PKR"}${calculateTax().toFixed(2)}</div>`
          : '<div style="padding:15px;"></div>'
      )
      .replace("{{total}}", calculateTotal().toFixed(2))
      .replace("{{business_footer}}", companyData.name || "Your Company")
      .replace("{{business_phones}}", companyData.phone || "Your Phone");
  };

  const closeAlert = () => {
    setAlert({ show: false, type: "", message: "" });
  };

  const steps = ["Company Selection", "Customer Information", "Edit Items", "Preview"];

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom">
              <h6 className="h6 mb-0 text-center">Edit Quote</h6>
            </div>
            <div className="card-body">
              {!canEdit && (
                <div className="alert alert-warning mb-4">
                  You do not have permission to edit this quote. Fields are read-only.
                </div>
              )}
             <div className="position-relative d-flex justify-content-between align-items-center mb-5" style={{ maxWidth: "600px", margin: "0 auto" }}>
  {steps.map((step, index) => (
    <div key={index} className="text-center flex-fill position-relative">
      <button
        className={`btn btn-sm ${
          currentStep > index + 1
            ? "btn-success"
            : currentStep === index + 1
            ? "btn-primary"
            : "btn-secondary"
        } rounded-circle z-1`}
        style={{ width: "45px", height: "45px", lineHeight: "1", position: "relative" }}
        onClick={() => handleStepClick(index + 1)}
        disabled={currentStep < index + 1}
      >
        {index + 1}
      </button>
      <div className="mt-2 small fw-semibold">{step}</div>
  
    </div>
  ))}
</div>


              {alert.show && (
                <div className={`alert alert-${alert.type} alert-dismissible fade show mb-4`} role="alert">
                  {alert.message}
                  <button type="button" className="btn-close" onClick={closeAlert} aria-label="Close"></button>
                </div>
              )}

              {currentStep === 1 && (
                <div className="p-3 rounded">
                  <h2 className="h6 mb-4">Company Selection</h2>
                  <div className="row g-3">
                    <div className="col-12">
                      <label htmlFor="companySelect" className="form-label fw-bold">
                        Select Company
                      </label>
                      <Select
                        inputId="companySelect"
                        options={companies}
                        value={selectedCompany}
                        onChange={handleCompanyChange}
                        className={`react-select ${errors.company ? "is-invalid" : ""}`}
                        classNamePrefix="select"
                        placeholder="Select a company"
                        isDisabled={!canEdit}
                      />
                      <div className="form-text">Select the company for this quote.</div>
                      {errors.company && <div className="invalid-feedback d-block">{errors.company}</div>}
                    </div>
                    {selectedCompany && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Company Name</label>
                          <input type="text" value={companyData.name} className="form-control form-control-lg" readOnly />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Address</label>
                          <input type="text" value={companyData.address} className="form-control form-control-lg" readOnly />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Email</label>
                          <input type="email" value={companyData.email} className="form-control form-control-lg" readOnly />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Phone</label>
                          <input type="text" value={companyData.phone} className="form-control form-control-lg" readOnly />
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-bold">VAT Registration No</label>
                          <input type="text" value={companyData.vatRegNo} className="form-control form-control-lg" readOnly />
                        </div>
                        <div className="col-12">
                          <label className="form-label fw-bold">Company Logo</label>
                          <div className="mt-3">
                            <img
                              src={companyData.logo || "./assets/images/logo.png"}
                              alt="Company Logo"
                              className="img-thumbnail img-fluid"
                              style={{ maxWidth: "150px" }}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="p-3 rounded">
                  <h2 className="h6 mb-4">Customer Information</h2>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label htmlFor="invoiceNumber" className="form-label fw-bold">
                        Quote Number
                      </label>
                      <input
                        type="text"
                        id="invoiceNumber"
                        disabled
                        value={invoiceData.invoiceNumber}
                        onChange={(e) => handleInvoiceChange("invoiceNumber", e.target.value)}
                        className={`form-control form-control-lg ${errors.invoiceNumber ? "is-invalid" : ""}`}
                      />
                      <div className="form-text">e.g., QT-001</div>
                      {errors.invoiceNumber && <div className="invalid-feedback">{errors.invoiceNumber}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="issuedDate" className="form-label fw-bold">
                        Issued Date
                      </label>
                      <input
                        type="date"
                        id="issuedDate"
                        value={invoiceData.issuedDate}
                        onChange={(e) => handleInvoiceChange("issuedDate", e.target.value)}
                        className={`form-control form-control-lg ${errors.issuedDate ? "is-invalid" : ""}`}
                        disabled={!canEdit}
                      />
                      <div className="form-text">Select the issue date.</div>
                      {errors.issuedDate && <div className="invalid-feedback">{errors.issuedDate}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="currency" className="form-label fw-bold">
                        Currency
                      </label>
                      <Select
                        inputId="currency"
                        options={currencyOptions}
                        value={selectedCurrency}
                        onChange={(option) => setSelectedCurrency(option)}
                        className={`react-select ${errors.currency ? "is-invalid" : ""}`}
                        classNamePrefix="select"
                        placeholder="Select currency"
                        isDisabled={!canEdit}
                      />
                      <div className="form-text">Select the currency for this quote.</div>
                      {errors.currency && <div className="invalid-feedback d-block">{errors.currency}</div>}
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="orderNumber" className="form-label fw-bold">
                        Order Number
                      </label>
                      <input
                        type="text"
                        id="orderNumber"
                        value={invoiceData.orderNumber}
                        onChange={(e) => handleInvoiceChange("orderNumber", e.target.value)}
                        className={`form-control form-control-lg ${errors.orderNumber ? "is-invalid" : ""}`}
                        disabled={!canEdit}
                      />
                      <div className="form-text">e.g., 12233</div>
                      {errors.orderNumber && <div className="invalid-feedback">{errors.orderNumber}</div>}
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label fw-bold">Customer Type</label>
                      <div className="d-flex">
                        <div className="form-check me-3">
                          <input
                            type="radio"
                            id="existingCustomer"
                            name="customerType"
                            value="existing"
                            checked={customerType === "existing"}
                            onChange={() => {
                              if (!canEdit) return;
                              setCustomerType("existing");
                              setInvoiceData({
                                ...invoiceData,
                                customerName: "",
                                customerAddress: "",
                                customerPhone: "",
                                email: "",
                                postcode: "",
                                latitude: "",
                                longitude: "",
                              });
                              setSelectedCustomer(null);
                              setErrors({});
                            }}
                            className="form-check-input"
                            disabled={!canEdit}
                          />
                          <label htmlFor="existingCustomer" className="form-check-label">
                            Existing Customer
                          </label>
                        </div>
                      </div>
                    </div>
                    {customerType === "existing" && (
                      <div className="col-12">
                        <label htmlFor="customerSelect" className="form-label fw-bold">
                          Select Customer
                        </label>
                        <Select
                          inputId="customerSelect"
                          options={customers}
                          value={selectedCustomer}
                          onChange={handleCustomerChange}
                          className={`react-select ${errors.customer ? "is-invalid" : ""}`}
                          classNamePrefix="select"
                          placeholder="Select a customer"
                          isDisabled={!canEdit}
                        />
                        <div className="form-text">Select the customer for this quote.</div>
                        {errors.customer && <div className="invalid-feedback d-block">{errors.customer}</div>}
                      </div>
                    )}
                    {customerType === "existing" && selectedCustomer && (
                      <>
                        <div className="col-md-6">
                          <label htmlFor="customerName" className="form-label fw-bold">
                            Customer Name
                          </label>
                          <input
                            type="text"
                            id="customerName"
                            value={invoiceData.customerName}
                            className="form-control form-control-lg"
                            readOnly
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="customerAddress" className="form-label fw-bold">
                            Customer Address
                          </label>
                          <input
                            type="text"
                            id="customerAddress"
                            value={invoiceData.customerAddress}
                            className="form-control form-control-lg"
                            readOnly
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="customerPhone" className="form-label fw-bold">
                            Customer Phone
                          </label>
                          <input
                            type="text"
                            id="customerPhone"
                            value={invoiceData.customerPhone}
                            className="form-control form-control-lg"
                            readOnly
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="email" className="form-label fw-bold">
                            Customer Email
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={invoiceData.email}
                            className="form-control form-control-lg"
                            readOnly
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="p-3 rounded bg-light">
                  <h2 className="h6 mb-4">Edit Items</h2>
                  <div className="card mb-4 shadow-sm">
                    <div className="card-body">
                      <div className="row g-3 align-items-end">
                        <div className="col-md-6">
                          <label htmlFor="itemType" className="form-label fw-bold">
                            Item Type
                          </label>
                          <select
                            id="itemType"
                            value={newItem.itemType}
                            onChange={(e) => {
                              if (!canEdit) return;
                              setNewItem({
                                itemType: e.target.value,
                                itemName: null,
                                qty: "",
                                unitPrice: "",
                                description: "",
                              });
                              if (quillInstance.current) {
                                quillInstance.current.root.innerHTML = "";
                                quillInstance.current.focus();
                              }
                              setErrors({});
                            }}
                            className="form-control form-control-lg"
                            disabled={!canEdit}
                          >
                            <option value="stock">Stock</option>
                            <option value="service">Service</option>
                          </select>
                          <div className="form-text">Select item type.</div>
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="itemName" className="form-label fw-bold">
                            {newItem.itemType === "stock" ? "Stock Item" : "Service"}
                          </label>
                          <Select
                            inputId="itemName"
                            options={newItem.itemType === "stock" ? stockList : optionsForServices}
                            value={newItem.itemName}
                            onChange={(e) => {
                              if (!canEdit) return;
                              if (newItem.itemType === "stock") {
                                onChangeStock(e);
                              } else {
                                onChangeService(e);
                              }
                            }}
                            className={`react-select ${errors.itemName ? "is-invalid" : ""}`}
                            classNamePrefix="select"
                            placeholder={`Select ${newItem.itemType === "stock" ? "stock item" : "service"}`}
                            isDisabled={!canEdit}
                          />
                          <div className="form-text">
                            Select {newItem.itemType === "stock" ? "an item from stock" : "a service"}.
                          </div>
                          {errors.itemName && <div className="invalid-feedback d-block">{errors.itemName}</div>}
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="unitPrice" className="form-label fw-bold">
                            Unit Price
                          </label>
                          <input
                            type="number"
                            id="unitPrice"
                            value={newItem.unitPrice}
                            onChange={(e) => handleItemChange("unitPrice", e.target.value)}
                            className={`form-control form-control-lg ${errors.unitPrice ? "is-invalid" : ""}`}
                            placeholder="Unit Price"
                            readOnly={newItem.itemType === "stock" && newItem.itemName}
                            disabled={!canEdit}
                          />
                          <div className="form-text">Enter the unit price ({selectedCurrency?.value || "PKR"}).</div>
                          {errors.unitPrice && <div className="invalid-feedback">{errors.unitPrice}</div>}
                        </div>
                        {newItem.itemType === "stock" && (
                          <div className="col-md-6">
                            <label htmlFor="qty" className="form-label fw-bold">
                              Quantity
                            </label>
                            <input
                              type="number"
                              id="qty"
                              value={newItem.qty}
                              onChange={(e) => handleItemChange("qty", e.target.value)}
                              className={`form-control form-control-lg ${errors.qty ? "is-invalid" : ""}`}
                              placeholder="Quantity"
                              disabled={!canEdit}
                            />
                            <div className="form-text">Enter the quantity.</div>
                            {errors.qty && <div className="invalid-feedback">{errors.qty}</div>}
                          </div>
                        )}
                        <div className="col-md-12">
                          <label htmlFor="description" className="form-label fw-bold">
                            Description
                          </label>
                          <div ref={quillRef} className={`ql-container ${errors.description ? "is-invalid" : ""}`} />
                          <div className="form-text">Provide a detailed description of the item or service (supports bold, bullet points, etc.).</div>
                          {errors.description && <div style={{ color: "#dc3545", fontSize: "0.875em" }}>{errors.description}</div>}
                        </div>
                        <div className={newItem.itemType === "stock" ? "col-md-4" : "col-md-4"}>
                          <label className="form-label fw-bold d-block"> </label>
                          <button onClick={addItem} className="btn btn-success btn-lg w-100" disabled={!canEdit}>
                            <Icon icon="simple-line-icons:plus" className="me-2" />
                            Add Item
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  {errors.items && <div className="alert alert-danger mb-4">{errors.items}</div>}
                  {invoiceItems.length > 0 && (
                    <div className="table-responsive mb-4">
                      <table className="table table-bordered table-hover">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">SL.</th>
                            <th scope="col">Item</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Unit Price</th>
                            <th scope="col">Price</th>
                            <th scope="col" className="text-center">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceItems.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>
                                {item.itemName?.label || item.itemName?.value}
                                <br />
                                <div dangerouslySetInnerHTML={{ __html: item.description }} />
                              </td>
                              <td>{item.qty}</td>
                              <td>
                                {selectedCurrency?.value || "PKR"}
                                {item.unitPrice.toFixed(2)}
                              </td>
                              <td>
                                {selectedCurrency?.value || "PKR"}
                                {item.price}
                              </td>
                              <td className="text-center">
                                <button
                                  onClick={() => removeItem(item.id)}
                                  className="btn btn-danger btn-sm"
                                  disabled={!canEdit}
                                >
                                  <Icon icon="ic:twotone-close" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="discount" className="form-label fw-bold">
                        Discount (%)
                      </label>
                      <input
                        type="number"
                        id="discount"
                        value={discount}
                        onChange={(e) => canEdit && setDiscount(Number(e.target.value))}
                        className="form-control form-control-lg"
                        placeholder="Enter discount"
                        disabled={!canEdit}
                      />
                      <div className="form-text">Enter discount percentage.</div>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="tax" className="form-label fw-bold">
                        VAT (%)
                      </label>
                      <input
                        type="number"
                        id="tax"
                        value={taxAmount}
                        onChange={(e) => canEdit && setTaxAmount(Number(e.target.value))}
                        className="form-control form-control-lg"
                        placeholder="Enter tax"
                        disabled={!canEdit}
                      />
                      <div className="form-text">Enter VAT percentage.</div>
                    </div>
                    <div className="col-12 text-end">
                      <p className="mb-1">
                        <strong>Total Net Amount:</strong> {selectedCurrency?.value || "PKR"}
                        {calculateSubtotal().toFixed(2)}
                      </p>
                      <p className="mb-1">
                        <strong>Discount:</strong> {selectedCurrency?.value || "PKR"}
                        {calculateDiscount().toFixed(2)}
                      </p>
                      <p className="mb-1">
                        <strong>VAT Amount:</strong> {selectedCurrency?.value || "PKR"}
                        {calculateTax().toFixed(2)}
                      </p>
                      <p className="mb-0">
                        <strong>Quote Total:</strong> {selectedCurrency?.value || "PKR"}
                        {calculateTotal().toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="p-3 rounded">
                  <h2 className="h6 mb-4">Quote Preview</h2>
                  <div className="border rounded p-4 bg-white" style={{ maxHeight: "600px", overflowY: "auto" }}>
                    <div dangerouslySetInnerHTML={{ __html: renderTemplate() }} />
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between mt-5">
                {currentStep > 1 && (
                  <button onClick={handleBack} className="btn btn-secondary btn-lg">
                    Back
                  </button>
                )}
                <div>
                  {currentStep < 4 && (
                    <button onClick={handleNext} className="btn btn-primary btn-lg">
                      Next
                    </button>
                  )}
                  {currentStep === 4 && (
                    <button
                      onClick={handleInvoiceSubmit}
                      className="btn btn-success btn-lg"
                      disabled={isSubmitting || !canEdit}
                    >
                      <Icon icon="simple-line-icons:check" className="me-2" />
                      {isSubmitting ? "Updating..." : "Update Quote"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteEditLayer;